import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import "./system.css";

type ReportPreset = {
  id: string;
  label: string;
  description: string;
};

const reportPresets: ReportPreset[] = [
  {
    id: "trading-performance",
    label: "Trading performance",
    description: "Success rate, PnL, drawdown indicators for executed orders.",
  },
  {
    id: "ai-news-analysis",
    label: "AI news analysis",
    description: "Coverage metrics for processed headlines and generated signals.",
  },
  {
    id: "system-uptime",
    label: "System uptime",
    description: "Service availability, incident tally, and MTTR/MTTA breakdowns.",
  },
];

export function SystemTab(): JSX.Element {
  return (
    <div className="system-root">
      <div className="system-column">
        <Card
          className="overview-card"
          title="Runtime overview"
          subtitle="Current environment, service footprint, and infrastructure posture."
        >
          <div className="system-card-list">
            <div className="system-info-row">
              <span>Environment</span>
              <span className="system-status-pill">Awaiting handshake</span>
            </div>
            <div className="system-info-row">
              <span>API base URL</span>
              <span>Configured once backend is connected.</span>
            </div>
            <div className="system-info-row">
              <span>Redis / Message bus</span>
              <span>Pending connection.</span>
            </div>
            <div className="system-info-row">
              <span>Release channel</span>
              <span>Stable · CI/CD pipeline not linked yet.</span>
            </div>
          </div>
        </Card>

        <Card
          className="overview-card"
          title="Configuration watch"
          subtitle="Critical variables and feature controls monitored for drift."
        >
          <div className="system-card-list">
            <div className="system-info-row">
              <span>API_PREFIX</span>
              <span>Will populate from backend settings.</span>
            </div>
            <div className="system-info-row">
              <span>REDIS_URL</span>
              <span>Awaiting secure vault integration.</span>
            </div>
            <div className="system-info-row">
              <span>FEATURE_FLAGS</span>
              <span>No flags toggled yet.</span>
            </div>
            <div className="system-info-row">
              <span>WEBHOOK_ENDPOINTS</span>
              <span>Connect integrations to register inbound targets.</span>
            </div>
          </div>
        </Card>

        <Card
          className="overview-card"
          title="Credential lifecycle"
          subtitle="API keys, secret rotation cadence, and ownership hints."
        >
          <div className="system-card-list">
            <div className="system-info-row">
              <span>Exchange API keys</span>
              <span>Waiting for vault handshake.</span>
            </div>
            <div className="system-info-row">
              <span>AI provider tokens</span>
              <span>Rotation policy pending.</span>
            </div>
            <div className="system-info-row">
              <span>Internal service credentials</span>
              <span>Managed by secrets store once connected.</span>
            </div>
            <Button type="button" variant="ghost">
              Open rotation playbook
            </Button>
          </div>
        </Card>

        <Card
          className="overview-card"
          title="Backups & recovery"
          subtitle="Snapshots and disaster-recovery readiness."
        >
          <div className="system-card-list">
            <div className="system-empty">
              No snapshot metadata yet. Link backup storage (S3, Azure Blob, etc.) to track schedules and retention.
            </div>
            <Button type="button" variant="ghost">
              Configure backup schedule
            </Button>
          </div>
        </Card>
      </div>

      <div className="system-column">
        <Card
          className="overview-card"
          title="Generate reports"
          subtitle="Run on-demand reports for trading performance, AI analysis, and system reliability."
        >
          <form className="system-report-form">
            <div className="system-report-row">
              <label>
                Report preset
                <select defaultValue="">
                  <option value="" disabled>
                    Select report
                  </option>
                  {reportPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Date range start
                <input type="date" />
              </label>
              <label>
                Date range end
                <input type="date" />
              </label>
            </div>

            <div className="system-report-row">
              <label>
                Time granularity
                <select defaultValue="daily">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>
              <label>
                Output format
                <select defaultValue="dashboard">
                  <option value="dashboard">In-app dashboard</option>
                  <option value="pdf">PDF export</option>
                  <option value="csv">CSV download</option>
                  <option value="webhook">Send to webhook</option>
                </select>
              </label>
            </div>

            <div className="system-report-footer">
              <div>
                <div>Preset preview</div>
                <div className="system-tag-cloud">
                  {reportPresets.map((preset) => (
                    <span key={`${preset.id}-preview`} className="system-tag">
                      {preset.label}
                    </span>
                  ))}
                </div>
              </div>
              <Button type="button" variant="primary">
                Prepare report
              </Button>
            </div>

            <div className="system-empty">
              Reports will render once the analytics endpoints (performance, AI insights, trade ledger) are connected.
            </div>
          </form>
        </Card>

        <Card
          className="overview-card"
          title="Role & access control"
          subtitle="Overview of platform roles, permissions, and security posture."
        >
          <div className="system-card-list">
            <table className="system-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Scope</th>
                  <th>Members</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={3} className="system-table__empty">
                    Roles will sync from the identity provider. Define admin, ops, and read-only policies to expose here.
                  </td>
                </tr>
              </tbody>
            </table>
            <Button type="button" variant="ghost">
              Manage identity provider
            </Button>
          </div>
        </Card>

        <Card
          className="overview-card"
          title="Audit & activity log"
          subtitle="Latest configuration changes and system events."
        >
          <div className="system-audit-log">
            <div className="system-audit-controls">
              <span>Filter by module once audit feed is wired.</span>
              <Button type="button" variant="ghost">
                Export CSV
              </Button>
            </div>
            <div className="system-empty">
              No audit records yet. When the backend publishes audit events they will appear here with user, scope, and timestamp metadata.
            </div>
          </div>
        </Card>

        <Card
          className="overview-card"
          title="Platform integrations"
          subtitle="Third-party clients and API consumers monitored for rate limits and usage."
        >
          <div className="system-card-list">
            <div className="system-empty">
              Client registry is empty. Register public API clients or webhook subscribers to monitor usage and rate limits.
            </div>
          </div>
        </Card>

        <Card
          className="overview-card"
          title="Maintenance windows"
          subtitle="Plan downtime, system migrations, or batch jobs."
        >
          <div className="system-maintenance-list">
            <div className="system-empty">
              No maintenance windows scheduled. Use backend scheduler integration to publish windows for ops teams.
            </div>
            <Button type="button" variant="ghost">
              Draft maintenance plan
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

